import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import unset from 'lodash-es/unset';
import { of } from 'rxjs';

import { TaskStatus } from 'task/model/task.status';
import { TaskType } from 'task/model/task.type';
import { IamUserTaskExportTaskOutput } from 'user/model/iam-user-task-export-task.output';
import { UserTaskExportTaskStatus } from 'user/model/user-task-export-task-status';
import { UserOutput } from 'user/model/user.output';
import { UserRepositoryPort } from 'user/port/user-repository.port';
import { UserTaskExportTask } from 'user/taskExportTask/user-task-export-task';
import { User } from 'user/user';
import { UserDocument } from 'user/user.store';

import { AppLocator } from '../../../app.locator';

export interface UserTaskExportTaskDocument {
	readonly id: string;
	readonly userId: string;
	readonly dueTimestamp: number;
	readonly updateTimestamp: number;
	readonly creationTimestamp: number;
	readonly exportedTasksType: TaskType;
	readonly status: UserTaskExportTaskStatus;
	readonly exportedTaskStatuses: TaskStatus[];
	readonly exportedTasksDocumentUrl: string;
	readonly exportedTasksParticipantId: string;
	readonly exportedTasksParticipantName: string;
}

export type UserTaskExportTaskState = EntityState<
	UserTaskExportTaskDocument,
	string
>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'userTaskExportTasks' })
export class UserTaskExportTaskStore extends EntityStore<UserTaskExportTaskState> {
	constructor() {
		super();
	}

	public get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public asEntity(document: UserTaskExportTaskDocument): UserTaskExportTask {
		return document
			? new UserTaskExportTask(
					document.id,
					document.userId,
					document.exportedTasksType,
					document.dueTimestamp,
					UserTaskExportTaskStatus[document.status],
					document.updateTimestamp || undefined,
					document.creationTimestamp || undefined,
					document.exportedTaskStatuses,
					document.exportedTasksDocumentUrl,
					document.exportedTasksParticipantId,
					document.exportedTasksParticipantName,
					document.userId
						? this._userRepositoryPort.observeUserById(document.userId)
						: of(null),
					document.exportedTasksParticipantId
						? this._userRepositoryPort.observeUserById(
								document.exportedTasksParticipantId
						  )
						: of(null)
			  )
			: null;
	}

	public asDocument(
		userTaskExportTask: UserTaskExportTask
	): UserTaskExportTaskDocument {
		const document = userTaskExportTask
			? ({
					exportedTasksParticipantName:
						userTaskExportTask.exportedTasksParticipantName,
					exportedTasksParticipantId: userTaskExportTask.exportedTasksParticipantId,
					exportedTasksDocumentUrl: userTaskExportTask.exportedTasksDocumentUrl,
					exportedTaskStatuses: userTaskExportTask.exportedTaskStatuses || [],
					exportedTasksType: userTaskExportTask.exportedTasksType,
					creationTimestamp: userTaskExportTask.creationTimestamp,
					updateTimestamp: userTaskExportTask.updateTimestamp,
					dueTimestamp: userTaskExportTask.dueTimestamp,
					userId: userTaskExportTask.userId,
					status: userTaskExportTask.status,
					id: userTaskExportTask.id,
			  } as UserTaskExportTask)
			: null;

		return document;
	}
}
